import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { leagueMeta, leagueOrder, siteInfo } from '@/lib/site';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/category/nba', label: 'NBA' },
  { to: '/category/cba', label: 'CBA' },
  { to: '/category/international', label: '国际赛' },
  { to: '/about', label: '关于我们' },
];

export default function SiteHeader() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (searchOpen) inputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
    setMenuOpen(false);
    setSearchOpen(false);
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled || menuOpen
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-background-50 border-b border-background-200'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
            <span className="w-9 h-9 rounded-md bg-primary-900 text-accent-500 flex items-center justify-center text-xl">
              <i className="ri-basketball-line" />
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg font-semibold tracking-wide text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="font-heading text-[10px] tracking-[0.24em] text-foreground-500 mt-1">
                {siteInfo.englishName}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
                    isActive
                      ? 'text-accent-700 bg-accent-50 font-medium'
                      : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <form
              onSubmit={submitSearch}
              className={`hidden md:flex items-center gap-2 rounded-full bg-background-100 border border-background-200 pl-3 pr-1 py-1 transition-all ${
                searchOpen ? 'w-64' : 'w-44'
              }`}
            >
              <i className="ri-search-line text-foreground-500 text-sm" />
              <input
                ref={inputRef}
                value={keyword}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setSearchOpen(false)}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索球队 / 赛事"
                aria-label="搜索赛事"
                name="q"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button
                type="submit"
                className="w-7 h-7 rounded-full bg-primary-900 text-background-50 flex items-center justify-center text-xs hover:bg-primary-700 transition-colors cursor-pointer"
                aria-label="提交搜索"
              >
                <i className="ri-arrow-right-line" />
              </button>
            </form>

            <Link
              to="/category/nba"
              className="hidden xl:inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-3.5 py-2 text-sm font-medium text-background-50 whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
            >
              <i className="ri-live-line" />
              正在直播
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="打开菜单"
              aria-expanded={menuOpen}
              className="lg:hidden w-9 h-9 rounded-md border border-background-300 text-foreground-800 flex items-center justify-center text-lg cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} />
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2 rounded-lg bg-background-100 border border-background-200 px-3 py-2">
              <i className="ri-search-line text-foreground-500 text-base" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索球队或赛事名称"
                aria-label="搜索赛事"
                name="q"
                className="flex-1 min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
              />
              <button type="submit" className="text-sm text-accent-700 font-medium whitespace-nowrap cursor-pointer">
                搜索
              </button>
            </form>

            <nav className="grid gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `flex items-center justify-between px-3 py-2.5 rounded-md text-sm ${
                      isActive ? 'bg-accent-50 text-accent-700 font-medium' : 'text-foreground-800'
                    }`
                  }
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-foreground-400" />
                </NavLink>
              ))}
            </nav>

            <div className="pt-3 border-t border-background-200">
              <p className="text-xs text-foreground-500 mb-2">联赛快捷入口</p>
              <div className="grid grid-cols-3 gap-2">
                {leagueOrder.map((key) => (
                  <Link
                    key={key}
                    to={`/category/${key}`}
                    className="flex flex-col items-center gap-1 rounded-md bg-background-100 py-3 text-xs text-foreground-800"
                  >
                    <i className={`${leagueMeta[key].icon} text-lg text-primary-700`} />
                    {leagueMeta[key].name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}